import dayjs from "dayjs";
import apiClient from "../../../helpers/apiClient";
import { getLocationMongoId } from "../../insurance/lib/insuranceSaleApi";
import { applyModifyInvoiceAdjustment, preserveInvoicePaymentHistory } from "./invoiceHelpers";
import { isBeforeScheduledPickup } from "./reservationHelpers";

export const EARLY_CHECK_IN_CHARGE_DESCRIPTION = "Early check-in";

const sumRegularChargeTotals = (invoice) =>
    (invoice.charges || [])
        .filter((charge) => charge.type !== "extraCharge")
        .reduce((sum, charge) => sum + (Number(charge.total) || 0), 0);

export async function buildEarlyCheckInInvoice(reservation) {
    const pickupDateTime = new Date().toISOString();
    const dropoffRaw = reservation.exactDropoffTime || reservation.dropoffDate;
    const dropoffDate = dropoffRaw ? new Date(dropoffRaw) : null;
    const baseInvoice = reservation.invoice;

    if (!baseInvoice) {
        throw new Error("Missing invoice on reservation for early check-in.");
    }

    if (!dropoffDate || Number.isNaN(dropoffDate.getTime())) {
        throw new Error("Missing drop-off date for early check-in invoice.");
    }

    if (!dayjs(dropoffDate).isAfter(dayjs(pickupDateTime))) {
        throw new Error("Drop-off must be after the new pickup time.");
    }

    const carId = reservation.carSelected?._id || reservation.carLinked?.[0]?._id;
    const category = reservation?.category?._id || reservation?.carSelected?.category?._id;
    const locationId = getLocationMongoId(reservation?.pickupLocation);
    const insuranceId = reservation?.addonsInsuranceId || "";
    const addonIdList = Array.isArray(reservation?.addons)
        ? reservation.addons.filter((id) => id && id !== insuranceId)
        : [];

    if (!carId) throw new Error("Missing vehicle for early check-in invoice.");
    if (!category) throw new Error("Missing vehicle category for early check-in invoice.");

    const { data } = await apiClient.post("/api/reservation/invoice-v2", {
        pickupDate: new Date(pickupDateTime),
        dropoffDate,
        category: String(category),
        location: locationId || "",
        addonIdList,
        carId: String(carId),
        insuranceId,
    });

    if (!data?.success) {
        throw new Error(data?.message || "Failed to generate early check-in invoice");
    }

    const earlyPickupInvoice = data.data.invoice || {};
    const earlyFee = Math.max(
        0,
        parseFloat((sumRegularChargeTotals(earlyPickupInvoice) - sumRegularChargeTotals(baseInvoice)).toFixed(2)),
    );

    const existingExtraCharges = (baseInvoice.charges || []).filter(
        (charge) => charge?.type === "extraCharge" && charge.description !== EARLY_CHECK_IN_CHARGE_DESCRIPTION,
    );
    const regularCharges = (baseInvoice.charges || []).filter((charge) => charge.type !== "extraCharge");

    const charges = [
        ...regularCharges,
        ...existingExtraCharges,
        ...(earlyFee > 0
            ? [
                  {
                      description: EARLY_CHECK_IN_CHARGE_DESCRIPTION,
                      unit: 1,
                      rate: earlyFee,
                      total: earlyFee,
                      type: "extraCharge",
                  },
              ]
            : []),
    ];

    const mergedInvoice = {
        ...baseInvoice,
        charges,
        taxes: earlyPickupInvoice.taxes,
        estimatedTotal: earlyPickupInvoice.estimatedTotal,
        total: earlyPickupInvoice.total,
    };

    const adjustedInvoice = applyModifyInvoiceAdjustment(mergedInvoice, reservation);
    const invoice = preserveInvoicePaymentHistory(adjustedInvoice, reservation);

    return { pickupDateTime, invoice };
}

export const shouldRecalculateEarlyCheckIn = (reservation, isEarlyCheckInRequest) =>
    isEarlyCheckInRequest && isBeforeScheduledPickup(reservation);

export const applyEarlyCheckInToReservation = (reservation, payload) => ({
    ...reservation,
    exactPickupTime: payload.pickupDateTime,
    invoice: payload.invoice,
});
